import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import PageDataState from '@/components/PageDataState'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'

import {
  filterSaleReferenceRows,
  formatSaleReferenceValue,
  type SaleReferenceColumnConfig,
  type SaleReferencePageConfig,
} from './saleReferenceConfig'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import {
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
  tableInnerHeight,
} from '@/theme/responsive'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

interface SaleReferenceGridProps {
  page: SaleReferencePageConfig
  rows: Record<string, unknown>[]
  search: string
  onSearchChange: (value: string) => void
  loading?: boolean
  error?: unknown
  onRetry?: () => void
}

type SaleRefMenuCol = SaleReferenceColumnConfig & { key: string; label: string }

export default function SaleReferenceGrid({
  page,
  rows,
  search,
  onSearchChange,
  loading = false,
  error,
  onRetry,
}: SaleReferenceGridProps) {
  const menuColumns = useMemo(
    (): SaleRefMenuCol[] => page.columns.map((c) => ({ ...c, key: c.field, label: c.headerName })),
    [page.columns],
  )
  const refColumnMode = `ref-${page.pageKey}`
  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_contract',
    prefKey: 'columns',
    subKey: refColumnMode,
    allColumns: menuColumns,
    widthsTableKey: `sale-contract.ref.${page.pageKey}`,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const [pageIndex, setPageIndex] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    field: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const { getWidth, startResize } = useColumnWidths(`sale-contract.ref.${page.pageKey}`)

  // Reset per-column filters when switching ref page (different schema).
  useEffect(() => {
    columnFilters.clearAll()
  }, [page.pageKey])  // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = useMemo(
    () =>
      filterSaleReferenceRows(rows, search, page.columns)
        .filter((row) => rowMatchesAllColumnFilters(row, columnFilters.filters))
        .map((row, index) => ({
          id: `${page.pageKey}-${index}`,
          ...row,
        }) as Record<string, unknown>),
    [page.pageKey, page.columns, rows, search, columnFilters.filters],
  )

  const popoverColumn = useMemo(
    () => (colPopover ? page.columns.find((c) => c.field === colPopover.field) ?? null : null),
    [colPopover, page.columns],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () =>
      popoverColumn
        ? inferColumnFilterKind(
            { key: popoverColumn.field, kind: popoverColumn.kind } as never,
            rows,
          )
        : 'text',
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, colPopover.field)
        : undefined,
    [popoverKind, colPopover, rows],
  )

  useEffect(() => {
    setPageIndex(0)
  }, [page.pageKey, search])

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, page.columns.map((column) => ({ field: column.field, kind: column.kind }))),
    [filteredRows, page.columns, sortState],
  )

  const visibleRows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = pageIndex * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [pageIndex, rowsPerPage, sortedRows])

  const isEmpty = !loading && !error && filteredRows.length === 0
  const totalValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((column) => column.kind === 'number')
          .map((column) => {
            let total = 0
            let count = 0
            for (const row of filteredRows) {
              const value = row[column.field]
              if (typeof value !== 'number' || !Number.isFinite(value)) continue
              total += value
              count += 1
            }
            return [column.field, count > 0 ? total : null] as const
          }),
      ),
    [filteredRows, visibleColumns],
  )
  const minTableWidth = useMemo(
    () => Math.max(1200, visibleColumns.reduce((total, column) => total + (column.width ?? column.minWidth ?? 160), 0)),
    [visibleColumns],
  )
  const gridKey = `${page.pageKey}:${rowsPerPage}:${pageIndex}:${filteredRows.length}:${visibleColumns.length}`
  const hasActiveFilters = Boolean(search.trim()) || columnFilters.hasAny
  const activeFilterCount = (search.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    onSearchChange('')
    columnFilters.clearAll()
    setRowsPerPage(-1)
    setPageIndex(0)
    filterPopover.close()
  }
  const toggleSort = (field: string) => {
    setSortState((current) => toggleTableSort(current, field))
    setPageIndex(0)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 0, flex: 1 }}>
      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            gap: { xs: 0.5, sm: 1 },
            flexWrap: 'wrap',
            minHeight: { xs: 24, sm: 30 },
            py: 0.125,
          }}
        >
          <FilterBar
            hasActiveFilters={hasActiveFilters}
            onClearAll={clearAllFilters}
            activeFilterCount={activeFilterCount}
          >
            <FilterButton
              label="Search"
              active={!!search.trim()}
              onClick={(e) => filterPopover.open('search', e)}
            />
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => openColMenu(e.currentTarget)}
              sx={filterBarBtnSx}
              title="Columns"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
            </Button>
          </FilterBar>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {loading && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Loaded: {rows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : pageIndex}
              onPageChange={(_, newPage) => setPageIndex(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPageIndex(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={search}
              onChange={(v) => {
                onSearchChange(v)
                setPageIndex(0)
              }}
              placeholder="Filter loaded rows..."
              label="Search reference"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.headerName ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.field] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.field, v)
            setPageIndex(0)
          }}
          options={popoverOptions}
        />

        <PageDataState
          loading={loading}
          error={error}
          empty={isEmpty}
          emptyMessage="No rows (or empty table)."
          onRetry={onRetry}
          minHeight={300}
        >
          <Box sx={tableInnerHeight(620)}>
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={visibleRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((column) => {
                    const w = getWidth(column.field)
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(column.field)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                      >
                        {column.headerName}
                        {getTableSortSuffix(sortState, column.field)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(column.field)}
                          onClick={(position) => setColPopover({ field: column.field, position })}
                          ariaLabel={`Filter ${column.headerName}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(column.field, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((column, index) => {
                    const total = column.kind === 'number' ? totalValues.get(column.field) : null
                    const text =
                      total == null ? '' : formatSaleReferenceValue(total, 'number', column.precision ?? 0)
                    const w = getWidth(column.field)
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : text}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: Record<string, unknown>) =>
                visibleColumns.map((column) => {
                  const value = formatSaleReferenceValue(row[column.field], column.kind, column.precision ?? 0)
                  const w = getWidth(column.field)
                  return (
                    <TableCell
                      key={column.field}
                      align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={value || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {value || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => <TableRow {...props} hover />,
              }}
            />
          </Box>
        </PageDataState>
      </Box>
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
